import React, { useState, useEffect } from 'react';
import api from '../api';
import { Line, Pie } from 'react-chartjs-2';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  Stack,
  Container,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Divider
} from '@mui/material';

function AnalyticsPage() {
  const [trailing, setTrailing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTrailing();
  }, []);

  const fetchTrailing = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/api/analytics/trailing');
      setTrailing(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load analytics');
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#6366f1' }} />
          </Box>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="lg">
          <Alert severity="error">{error}</Alert>
        </Container>
      </Box>
    );
  }

  const averageMonthly = trailing.trailing.totalSpend / trailing.trailing.monthCount;

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        {/* Page Title */}
        <Typography variant="h5" fontWeight={700} color="text.primary" mb={4}>
          Trailing 12 Months Analysis
        </Typography>

        {/* Summary Cards */}
        <Grid container spacing={2} mb={4}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Total (12 Months)
                </Typography>
                <Typography variant="h5" fontWeight={700} color="text.primary">
                  {formatCurrency(trailing.trailing.totalSpend)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Average Monthly
                </Typography>
                <Typography variant="h5" fontWeight={700} color="text.primary">
                  {formatCurrency(averageMonthly)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Months Analyzed
                </Typography>
                <Typography variant="h5" fontWeight={700} color="text.primary">
                  {trailing.trailing.monthCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)' }}>
              <Typography variant="h6" fontWeight={600} color="text.primary" mb={2}>
                Monthly Spending Trend
              </Typography>
              <Box sx={{ position: 'relative', height: 300 }}>
                <Line data={trailing.charts.line} options={trailing.charts.line.options} />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)' }}>
              <Typography variant="h6" fontWeight={600} color="text.primary" mb={2}>
                Category Distribution (12 Months)
              </Typography>
              <Box sx={{ position: 'relative', height: 300 }}>
                <Pie data={trailing.charts.pie} />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Monthly Breakdown Table */}
        <Paper sx={{ borderRadius: 2, boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
          <Box sx={{ p: 3, pb: 2 }}>
            <Typography variant="h6" fontWeight={600} color="text.primary">
              Monthly Breakdown
            </Typography>
          </Box>
          <Divider />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Month</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Total Spending</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Transactions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trailing.trailing.months.map((month, index) => {
                  const date = new Date(month.month);
                  const monthName = date.toLocaleString('default', { month: 'long', year: 'numeric' });
                  return (
                    <TableRow key={index} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell>{monthName}</TableCell>
                      <TableCell>{formatCurrency(month.totalSpend)}</TableCell>
                      <TableCell>{month.transactionCount}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>
    </Box>
  );
}

export default AnalyticsPage;
